import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Me, Session } from '@rydres/api-client';
import { api } from './api.js';

interface SessionState {
  ready: boolean;
  session: Session | null;
  me: Me | null;
  reloadMe: () => Promise<Me | null>;
  requestCode: (phone: string) => Promise<void>;
  signIn: (phone: string, code: string) => Promise<Session>;
  signOut: () => Promise<void>;
}
const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [me, setMe] = useState<Me | null>(null);

  const reloadMe = useCallback(async () => {
    try {
      const m = await api.me();
      setMe(m);
      return m;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    void api.init().then(async (s) => {
      if (s) {
        const r = await api.refresh().catch(() => null);
        setSession(r ?? s);
        await reloadMe();
      }
      setReady(true);
    });
    const onOut = () => {
      setSession(null);
      setMe(null);
    };
    window.addEventListener('rydres:signed-out', onOut);
    return () => window.removeEventListener('rydres:signed-out', onOut);
  }, [reloadMe]);

  const requestCode = useCallback(async (phone: string) => void (await api.requestOtp(phone)), []);
  const signIn = useCallback(
    async (phone: string, code: string) => {
      const s = await api.verifyOtp(phone, code);
      setSession(s);
      await reloadMe();
      return s;
    },
    [reloadMe],
  );
  const signOut = useCallback(async () => {
    await api.signOut();
    setSession(null);
    setMe(null);
  }, []);
  const value = useMemo(
    () => ({ ready, session, me, reloadMe, requestCode, signIn, signOut }),
    [ready, session, me, reloadMe, requestCode, signIn, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside provider');
  return v;
}
